import { useEffect, useMemo, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Copy, Mail, Phone, ChevronRight, ArrowUpRight, Pencil, Save, X, Sparkles, MapPin, Check, GraduationCap, BookOpen, Heart, Handshake, Ban, Trash2, ThumbsUp } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { copyToClipboard, deptAvatarClass, deptBadgeClass, formatRoDate } from "@/lib/employees";
import { useIdentity } from "@/lib/identity";
import SkillsEditor from "@/components/SkillsEditor";
import {
  MentorProfileEditor,
  MenteeSkillsEditor,
  MentorStatusBadge,
  RequestMentorshipButton,
} from "@/components/Mentorship";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PHONE_RE = /^[0-9 +()\-]{0,20}$/;


export default function ProfileModal({
  employees,
  employeeId,
  onClose,
  onOpenOther,
  onEmployeeUpdated,
  onEmployeeDeleted,
  allSkills,
  onSkillClick,
}) {
  const { me } = useIdentity();
  const employee = useMemo(
    () => employees.find((e) => e.id === employeeId) || null,
    [employees, employeeId]
  );

  const [editingPhone, setEditingPhone] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState("");
  const [savingPhone, setSavingPhone] = useState(false);
  const [editingLocation, setEditingLocation] = useState(false);
  const [savingLocation, setSavingLocation] = useState(false);
  const [editingSkills, setEditingSkills] = useState(false);
  const [editingMentor, setEditingMentor] = useState(false);
  const [editingMentee, setEditingMentee] = useState(false);

  // reset edit state when switching profile
  useEffect(() => {
    setEditingPhone(false);
    setEditingLocation(false);
    setEditingSkills(false);
    setEditingMentor(false);
    setEditingMentee(false);
    setPhoneDraft(employee?.phone || "");
  }, [employeeId, employee?.phone]);

  const manager = useMemo(() => {
    if (!employee || !employee.manager_id) return null;
    return employees.find((e) => e.id === employee.manager_id) || null;
  }, [employees, employee]);

  const reports = useMemo(() => {
    if (!employee) return [];
    return employees
      .filter((e) => e.manager_id === employee.id)
      .sort((a, b) => a.name.localeCompare(b.name, "ro"));
  }, [employees, employee]);

  // Arrow keys nav between siblings (only when not editing).
  useEffect(() => {
    if (!employee || editingPhone || editingSkills) return;
    const onKey = (e) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      const siblings = employees
        .filter((x) => x.department === employee.department)
        .sort((a, b) => a.name.localeCompare(b.name, "ro"));
      if (siblings.length < 2) return;
      const idx = siblings.findIndex((x) => x.id === employee.id);
      const next = e.key === "ArrowRight"
        ? siblings[(idx + 1) % siblings.length]
        : siblings[(idx - 1 + siblings.length) % siblings.length];
      onOpenOther(next.id);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [employee, employees, onOpenOther, editingPhone, editingSkills]);

  const handleCopy = (text, kind) => {
    copyToClipboard(text)
      .then(() => {
        toast.success(kind === "email" ? "Email copiat în clipboard!" : "Telefon copiat în clipboard!");
      })
      .catch(() => toast.error("Nu am putut copia"));
  };

  const savePhone = async () => {
    const v = phoneDraft.trim();
    if (v && !PHONE_RE.test(v)) {
      toast.error("Format telefon invalid");
      return;
    }
    setSavingPhone(true);
    try {
      const r = await axios.patch(`${API}/employees/${employee.id}/phone`, { phone: v });
      toast.success("Telefon actualizat");
      onEmployeeUpdated?.(r.data);
      setEditingPhone(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSavingPhone(false);
    }
  };

  const saveLocation = async (loc) => {
    setSavingLocation(true);
    try {
      const r = await axios.patch(`${API}/employees/${employee.id}/location`, { location: loc });
      toast.success(loc ? `Locație setată: ${loc}` : "Locație eliminată");
      onEmployeeUpdated?.(r.data);
      setEditingLocation(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    } finally {
      setSavingLocation(false);
    }
  };

  const isMe = me && employee && me.id === employee.id;
  // Editing open to everyone (open editing mode).
  const canEditSelf = true;
  const [editingHobby, setEditingHobby] = useState(false);
  const [hobbyDraft, setHobbyDraft] = useState(employee?.hobbies || []);
  const [hobbyInput, setHobbyInput] = useState("");
  const [savingHobby, setSavingHobby] = useState(false);
  useEffect(() => { setEditingHobby(false); setHobbyDraft(employee?.hobbies || []); setHobbyInput(""); }, [employeeId, employee?.hobbies]);

  const addHobby = (val) => {
    const v = (val || "").trim().replace(/\s+/g, " ").slice(0, 40);
    if (!v) return;
    setHobbyDraft((prev) => (prev.some((h) => h.toLowerCase() === v.toLowerCase()) ? prev : [...prev, v]));
    setHobbyInput("");
  };
  const removeHobby = (h) => setHobbyDraft((prev) => prev.filter((x) => x !== h));

  const saveHobby = async () => {
    // flush pending typed hobby
    let finalHobbies = hobbyDraft;
    const pending = hobbyInput.trim().replace(/\s+/g, " ").slice(0, 40);
    if (pending && !finalHobbies.some((h) => h.toLowerCase() === pending.toLowerCase())) {
      finalHobbies = [...finalHobbies, pending];
    }
    setSavingHobby(true);
    try {
      const r = await axios.patch(`${API}/employees/${employee.id}/hobby`, { hobbies: finalHobbies });
      setHobbyDraft(finalHobbies);
      setHobbyInput("");
      toast.success("Hobby-uri actualizate");
      onEmployeeUpdated?.(r.data);
      setEditingHobby(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    } finally { setSavingHobby(false); }
  };

  const [editingStatus, setEditingStatus] = useState(false);
  const [inactiveDraft, setInactiveDraft] = useState(!!employee?.inactive);
  const [sinceDraft, setSinceDraft] = useState(employee?.inactive_since || "");
  const [savingStatus, setSavingStatus] = useState(false);
  useEffect(() => {
    setEditingStatus(false);
    setInactiveDraft(!!employee?.inactive);
    setSinceDraft(employee?.inactive_since || "");
  }, [employeeId, employee?.inactive, employee?.inactive_since]);

  const saveStatus = async () => {
    if (inactiveDraft && !sinceDraft) { toast.error("Alege data încetării activității"); return; }
    setSavingStatus(true);
    try {
      const r = await axios.patch(`${API}/employees/${employee.id}/status`, {
        inactive: inactiveDraft,
        inactive_since: inactiveDraft ? sinceDraft : "",
      });
      toast.success(inactiveDraft ? "Coleg marcat ca inactiv" : "Coleg marcat ca activ");
      onEmployeeUpdated?.(r.data);
      setEditingStatus(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la salvare");
    } finally { setSavingStatus(false); }
  };

  const [deleting, setDeleting] = useState(false);
  const handleDelete = async () => {    if (!window.confirm(`Ștergi definitiv pe ${employee.name}? Această acțiune nu poate fi anulată.`)) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/employees/${employee.id}`);
      toast.success(`${employee.name} a fost șters din listă.`);
      onEmployeeDeleted?.(employee.id);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la ștergere");
    } finally { setDeleting(false); }
  };

  const [endorsingSkill, setEndorsingSkill] = useState(null);
  const [likes, setLikes] = useState(() => {
    try { return JSON.parse(localStorage.getItem("ezipper-likes") || "{}"); } catch { return {}; }
  });
  const iLiked = (skill) => !!likes[`${employee?.id}:${skill.toLowerCase()}`];
  const endorseCount = (skill) => {
    const e = (employee?.endorsements || []).find((x) => (x.skill || "").toLowerCase() === skill.toLowerCase());
    return e ? (e.count || 0) : 0;
  };
  const endorse = async (skill) => {
    const key = `${employee.id}:${skill.toLowerCase()}`;
    const liked = !!likes[key];
    const delta = liked ? -1 : 1;
    setEndorsingSkill(skill);
    try {
      const r = await axios.post(`${API}/employees/${employee.id}/endorse`, { skill, delta });
      onEmployeeUpdated?.(r.data);
      const nl = { ...likes };
      if (liked) delete nl[key]; else nl[key] = true;
      setLikes(nl);
      try { localStorage.setItem("ezipper-likes", JSON.stringify(nl)); } catch (err) { console.warn("Likes: salvare eșuată", err); }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare la apreciere");
    } finally { setEndorsingSkill(null); }
  };

  return (
    <Dialog open={!!employee} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent
        data-testid="profile-modal"
        className="max-w-2xl p-0 overflow-hidden bg-white dark:bg-zinc-900"
      >
        {employee && (
          <div className="flex flex-col max-h-[85vh]">
            {/* Header */}
            <div className="p-6 sm:p-8 border-b border-border">
              <div className="flex items-start gap-5">
                <div
                  className={`flex-shrink-0 w-20 h-20 rounded-full flex items-center justify-center text-xl font-semibold ${deptAvatarClass(employee.department)}`}
                >
                  {employee.initials}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <DialogTitle className="text-2xl font-semibold tracking-tight text-zinc-900 dark:text-zinc-50">
                      {employee.name}
                    </DialogTitle>
                    {isMe && (
                      <span className="text-[10px] uppercase tracking-[0.2em] font-medium px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                        Tu
                      </span>
                    )}
                    <MentorStatusBadge employee={employee} size="md" />
                  </div>
                  <DialogDescription className="sr-only">Detalii profil pentru {employee.name}</DialogDescription>
                  <p className="mt-1 text-zinc-600 dark:text-zinc-400">{employee.role}</p>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full ring-1 ring-inset ${deptBadgeClass(employee.department)}`}>
                      {employee.department}
                    </span>
                    {employee.is_collaborator && (
                      <span data-testid="profile-collaborator-badge" className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300 ring-1 ring-inset ring-amber-300/50 dark:ring-amber-800/50">
                        <Handshake className="h-3 w-3" /> Colaborator
                      </span>
                    )}
                    {employee.inactive && (
                      <span data-testid="profile-inactive-badge" className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full bg-zinc-200 text-zinc-600 dark:bg-zinc-700 dark:text-zinc-300 ring-1 ring-inset ring-zinc-300 dark:ring-zinc-600">
                        <Ban className="h-3 w-3" /> Inactiv{employee.inactive_since ? ` din ${formatRoDate(employee.inactive_since)}` : ""}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6 sm:p-8 space-y-5 overflow-y-auto">
              {/* Contact */}
              <section>
                <h4 className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-3">
                  Contact
                </h4>
                <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-zinc-50 dark:bg-zinc-800/40">
                    <a
                      href={employee.email ? `mailto:${employee.email}` : undefined}
                      data-testid="profile-email-link"
                      className={`flex items-center gap-3 min-w-0 text-sm ${employee.email ? "text-zinc-900 dark:text-zinc-100 hover:underline" : "text-zinc-500 dark:text-zinc-400 pointer-events-none"}`}
                    >
                      <Mail className="h-4 w-4 flex-shrink-0" />
                      <span className="truncate">{employee.email || "—"}</span>
                    </a>
                    {employee.email && (
                      <button
                        data-testid="copy-email-btn"
                        onClick={() => handleCopy(employee.email, "email")}
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border border-border hover:bg-white dark:hover:bg-zinc-700 transition-colors duration-150"
                      >
                        <Copy className="h-3.5 w-3.5" /> Copiază
                      </button>
                    )}
                  </div>

                  {/* Phone — editable by anyone */}
                  <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-zinc-50 dark:bg-zinc-800/40">
                    {editingPhone ? (
                      <div className="flex-1 flex items-center gap-2">
                        <Phone className="h-4 w-4 flex-shrink-0 text-zinc-400" />
                        <input
                          data-testid="phone-edit-input"
                          autoFocus
                          type="tel"
                          value={phoneDraft}
                          onChange={(e) => setPhoneDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); savePhone(); }
                            else if (e.key === "Escape") { setEditingPhone(false); setPhoneDraft(employee.phone || ""); }
                          }}
                          placeholder="0712 345 678"
                          className="flex-1 h-8 px-2 rounded-md border border-border bg-white dark:bg-zinc-950 text-sm text-zinc-900 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 dark:focus-visible:ring-zinc-100"
                        />
                        <button
                          data-testid="phone-save-btn"
                          onClick={savePhone}
                          disabled={savingPhone}
                          className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1.5 rounded-md bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 hover:opacity-90 disabled:opacity-50"
                        >
                          <Save className="h-3.5 w-3.5" />
                        </button>
                        <button
                          data-testid="phone-cancel-btn"
                          onClick={() => { setEditingPhone(false); setPhoneDraft(employee.phone || ""); }}
                          className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1.5 rounded-md border border-border hover:bg-white dark:hover:bg-zinc-700"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ) : (
                      <>
                        <a
                          href={employee.phone ? `tel:${employee.phone}` : undefined}
                          data-testid="profile-phone-link"
                          className={`flex items-center gap-3 min-w-0 text-sm ${employee.phone ? "text-zinc-900 dark:text-zinc-100 hover:underline" : "text-zinc-500 dark:text-zinc-400 pointer-events-none"}`}
                        >
                          <Phone className="h-4 w-4 flex-shrink-0" />
                          <span>{employee.phone || "—"}</span>
                        </a>
                        <div className="flex items-center gap-1.5">
                          {employee.phone && (
                            <button
                              data-testid="copy-phone-btn"
                              onClick={() => handleCopy(employee.phone, "phone")}
                              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border border-border hover:bg-white dark:hover:bg-zinc-700 transition-colors duration-150"
                            >
                              <Copy className="h-3.5 w-3.5" /> Copiază
                            </button>
                          )}
                          {canEditSelf && (
                            <button
                              data-testid="edit-phone-btn"
                              onClick={() => setEditingPhone(true)}
                              title="Editează telefon"
                              className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border border-border hover:bg-white dark:hover:bg-zinc-700 transition-colors duration-150"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                              {employee.phone ? "" : <span>Adaugă</span>}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                  </div>

                  {/* Location — Bucuresti / Cluj */}
                  <div className="flex items-center justify-between gap-3 p-3 rounded-lg border border-border bg-zinc-50 dark:bg-zinc-800/40">
                    {editingLocation ? (
                      <div className="flex-1 flex items-center gap-2 flex-wrap">
                        <MapPin className="h-4 w-4 flex-shrink-0 text-zinc-400" />
                        <span className="text-xs text-zinc-500">Alege:</span>
                        {["București", "Cluj"].map((opt) => (
                          <button
                            key={opt}
                            data-testid={`location-option-${opt}`}
                            disabled={savingLocation}
                            onClick={() => saveLocation(opt)}
                            className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full border transition-colors ${
                              employee.location === opt
                                ? "bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 border-zinc-900 dark:border-zinc-100"
                                : "border-border hover:bg-white dark:hover:bg-zinc-700"
                            }`}
                          >
                            {employee.location === opt && <Check className="h-3 w-3" />}
                            {opt}
                          </button>
                        ))}
                        {employee.location && (
                          <button
                            data-testid="location-clear-btn"
                            disabled={savingLocation}
                            onClick={() => saveLocation("")}
                            className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-100"
                          >
                            <X className="h-3 w-3" /> șterge
                          </button>
                        )}
                        <button
                          data-testid="location-close-btn"
                          onClick={() => setEditingLocation(false)}
                          className="ml-auto inline-flex items-center text-xs px-2 py-1 rounded-md border border-border hover:bg-white dark:hover:bg-zinc-700"
                        >
                          OK
                        </button>
                      </div>
                    ) : (
                      <>
                        <div data-testid="profile-location" className={`flex items-center gap-3 min-w-0 text-sm ${employee.location ? "text-zinc-900 dark:text-zinc-100" : "text-zinc-500 dark:text-zinc-400"}`}>
                          <MapPin className="h-4 w-4 flex-shrink-0" />
                          <span>{employee.location || "Locație nesetată"}</span>
                        </div>
                        {canEditSelf && (
                          <button
                            data-testid="edit-location-btn"
                            onClick={() => setEditingLocation(true)}
                            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border border-border hover:bg-white dark:hover:bg-zinc-700 transition-colors duration-150"
                          >
                            <Pencil className="h-3.5 w-3.5" />
                            {employee.location ? "" : <span>Setează</span>}
                          </button>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </section>

              {/* Hobby */}
              <section data-testid="profile-hobby-section">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 inline-flex items-center gap-2">
                    <Heart className="h-3 w-3" /> Hobby & Interese
                  </h4>
                  {!editingHobby && (
                    <button
                      data-testid="edit-hobby-btn"
                      onClick={() => setEditingHobby(true)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border border-border hover:bg-zinc-50 dark:hover:bg-zinc-800"
                    >
                      <Pencil className="h-3 w-3" />
                      {(employee.hobbies || []).length ? "Editează" : "Adaugă"}
                    </button>
                  )}
                </div>
                {editingHobby ? (
                  <div className="space-y-2">
                    <div className="flex flex-wrap gap-1.5">
                      {hobbyDraft.length === 0 && <span className="text-[11px] text-zinc-400 italic">Niciun hobby adăugat.</span>}
                      {hobbyDraft.map((h) => (
                        <span key={h} data-testid={`hobby-pill-${h}`} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 text-xs font-medium rounded-full bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300">
                          {h}
                          <button onClick={() => removeHobby(h)} className="ml-0.5 inline-flex items-center justify-center h-4 w-4 rounded-full hover:bg-black/10 dark:hover:bg-white/10">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                    <input
                      data-testid="hobby-input"
                      value={hobbyInput}
                      onChange={(e) => setHobbyInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addHobby(hobbyInput); } }}
                      maxLength={40}
                      placeholder="Scrie un hobby și apasă Enter (ex: Drumeții montane)"
                      className="w-full h-9 px-3 rounded-lg border border-border bg-white dark:bg-zinc-950 text-sm focus:outline-none focus-visible:ring-1 focus-visible:ring-zinc-900 dark:focus-visible:ring-zinc-100"
                    />
                    <div className="flex items-center gap-2 text-[11px] text-zinc-500">
                      <button
                        data-testid="hobby-save-btn"
                        onClick={saveHobby}
                        disabled={savingHobby}
                        className="inline-flex items-center gap-1 px-3 h-8 rounded-md bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 text-xs font-medium hover:opacity-90 disabled:opacity-50"
                      >
                        <Save className="h-3.5 w-3.5" /> Salvează
                      </button>
                      <button
                        onClick={() => { setEditingHobby(false); setHobbyDraft(employee.hobbies || []); setHobbyInput(""); }}
                        className="inline-flex items-center gap-1 px-3 h-8 rounded-md border border-border text-xs"
                      >
                        Anulează
                      </button>
                    </div>
                  </div>
                ) : (
                  (employee.hobbies || []).length ? (
                    <div data-testid="profile-hobby" className="flex flex-wrap gap-1.5">
                      {employee.hobbies.map((h) => (
                        <span key={h} className="inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">
                          {h}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-zinc-500 dark:text-zinc-400 italic">Niciun hobby adăugat încă.</p>
                  )
                )}
              </section>
              {/* Activitate / Încetare activitate */}
              <section data-testid="profile-status-section">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">
                    Activitate
                  </h4>
                  {!editingStatus && (
                    <button
                      data-testid="edit-status-btn"
                      onClick={() => setEditingStatus(true)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border border-border hover:bg-zinc-50 dark:hover:bg-zinc-800"
                    >
                      <Pencil className="h-3 w-3" /> Editează
                    </button>
                  )}
                </div>
                {editingStatus ? (
                  <div className="space-y-3 p-3 rounded-lg border border-border bg-zinc-50 dark:bg-zinc-800/40">
                    <label className="flex items-center gap-2.5 cursor-pointer">
                      <input
                        data-testid="status-inactive-checkbox"
                        type="checkbox"
                        checked={inactiveDraft}
                        onChange={(e) => setInactiveDraft(e.target.checked)}
                        className="h-4 w-4 rounded border-border accent-zinc-700"
                      />
                      <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">A încetat activitatea</span>
                    </label>
                    {inactiveDraft && (
                      <div>
                        <label className="text-[11px] uppercase tracking-[0.15em] text-zinc-500 dark:text-zinc-400 block mb-1">Data încetării</label>
                        <input
                          data-testid="status-date-input"
                          type="date"
                          value={sinceDraft}
                          onChange={(e) => setSinceDraft(e.target.value)}
                          className="h-9 px-3 rounded-lg border border-border bg-white dark:bg-zinc-950 text-sm focus:outline-none"
                        />
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <button
                        data-testid="status-save-btn"
                        onClick={saveStatus}
                        disabled={savingStatus}
                        className="inline-flex items-center gap-1 px-3 h-8 rounded-md bg-zinc-900 text-white dark:bg-zinc-100 dark:text-zinc-900 text-xs font-medium hover:opacity-90 disabled:opacity-50"
                      >
                        <Save className="h-3.5 w-3.5" /> Salvează
                      </button>
                      <button
                        onClick={() => { setEditingStatus(false); setInactiveDraft(!!employee.inactive); setSinceDraft(employee.inactive_since || ""); }}
                        className="inline-flex items-center gap-1 px-3 h-8 rounded-md border border-border text-xs"
                      >
                        Anulează
                      </button>
                    </div>
                  </div>
                ) : (
                  employee.inactive ? (
                    <p data-testid="profile-status-text" className="text-sm text-zinc-600 dark:text-zinc-400 inline-flex items-center gap-1.5">
                      <Ban className="h-3.5 w-3.5" /> Și-a încetat activitatea{employee.inactive_since ? ` din ${formatRoDate(employee.inactive_since)}` : ""}.
                    </p>
                  ) : (
                    <p className="text-sm text-emerald-700 dark:text-emerald-400">Activ.</p>
                  )
                )}
                {me?.is_admin && (
                  <div className="mt-4 pt-3 border-t border-dashed border-red-200 dark:border-red-900/40">
                    <button
                      data-testid="delete-employee-btn"
                      onClick={handleDelete}
                      disabled={deleting}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-3 h-8 rounded-md border border-red-300 dark:border-red-800 text-red-700 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 disabled:opacity-50 transition-colors"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> {deleting ? "Se șterge..." : "Șterge angajatul"}
                    </button>
                    <p className="mt-1.5 text-[11px] text-zinc-400">Acțiune definitivă, doar pentru administratori.</p>
                  </div>
                )}
              </section>

              <section data-testid="profile-skills-section">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 inline-flex items-center gap-2">
                    <Sparkles className="h-3 w-3" /> Competențe
                  </h4>
                  {!editingSkills && (
                    <button
                      data-testid="edit-skills-btn"
                      onClick={() => setEditingSkills(true)}
                      className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border border-border hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                    >
                      <Pencil className="h-3.5 w-3.5" /> Editează
                    </button>
                  )}
                </div>

                {editingSkills ? (
                  <SkillsEditor
                    employee={employee}
                    allSkills={allSkills || []}
                    onSaved={(updated) => {
                      onEmployeeUpdated?.(updated);
                      setEditingSkills(false);
                    }}
                    onCancel={() => setEditingSkills(false)}
                  />
                ) : (
                  <>
                    {(!employee.skills || employee.skills.length === 0) ? (
                      <p className="text-sm text-zinc-500 dark:text-zinc-400 italic">
                        Nicio competență adăugată. Apasă „Adaugă" pentru a începe.
                      </p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {employee.skills.map((s) => {
                          const count = endorseCount(s);
                          const mine = iLiked(s);
                          return (
                            <span
                              key={s}
                              data-testid={`profile-skill-chip-${s}`}
                              className="inline-flex items-center rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden ring-1 ring-inset ring-transparent"
                            >
                              <button
                                data-testid={`profile-skill-${s}`}
                                onClick={() => onSkillClick?.(s)}
                                className="px-2.5 py-1 text-xs font-medium text-zinc-700 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors"
                              >
                                {s}
                              </button>
                              <button
                                data-testid={`endorse-btn-${s}`}
                                onClick={() => endorse(s)}
                                disabled={endorsingSkill === s}
                                title={mine ? "Retrage aprecierea" : "Apreciază această competență"}
                                className={`inline-flex items-center gap-1 px-2 py-1 text-[11px] font-semibold border-l border-zinc-200 dark:border-zinc-700 transition-colors disabled:opacity-60 ${
                                  mine
                                    ? "bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300"
                                    : "text-zinc-500 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700"
                                }`}
                              >
                                <ThumbsUp className={`h-3 w-3 ${mine ? "fill-current" : ""}`} />
                                {count > 0 && <span data-testid={`endorse-count-${s}`}>{count}</span>}
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </>
                )}
              </section>

              {/* Mentorship — opt-in (own profile only) OR request button (other's profile) */}
              <section data-testid="profile-mentor-section">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 inline-flex items-center gap-2">
                    <GraduationCap className="h-3 w-3" /> Mentorat
                  </h4>
                </div>

                {editingMentor && (
                  <MentorProfileEditor
                    employee={employee}
                    allSkills={allSkills || []}
                    onSaved={(updated) => { onEmployeeUpdated?.(updated); setEditingMentor(false); }}
                    onCancel={() => setEditingMentor(false)}
                  />
                )}
                {editingMentee && (
                  <MenteeSkillsEditor
                    employee={employee}
                    allSkills={allSkills || []}
                    onSaved={(updated) => { onEmployeeUpdated?.(updated); setEditingMentee(false); }}
                    onCancel={() => setEditingMentee(false)}
                  />
                )}

                {!editingMentor && !editingMentee && (
                  <div className="space-y-2 text-sm">
                    {/* Mentor info */}
                    <div className="p-3 rounded-lg border border-border bg-zinc-50 dark:bg-zinc-800/40">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-[11px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">
                          {isMe ? "Eu ca mentor" : `${employee.name.split(" ")[0]} ca mentor`}
                        </div>
                        <div className="flex items-center gap-1.5">
                          <MentorStatusBadge employee={employee} />
                          <button
                            data-testid="open-mentor-editor"
                            onClick={() => setEditingMentor(true)}
                            className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border border-border hover:bg-white dark:hover:bg-zinc-800"
                          >
                            <Pencil className="h-3 w-3" /> Editează
                          </button>
                        </div>
                      </div>
                      {employee.mentor_active && (employee.mentor_skills || []).length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {(employee.mentor_skills || []).map((s) => (
                            <span key={s} data-testid={`mentor-offer-${s}`} className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-md bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300">
                              {s}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-1.5 text-[11px] text-zinc-500 dark:text-zinc-400 italic">
                          {isMe ? "Nu te-ai înscris încă în pool-ul de mentori." : "Nu este înscris ca mentor."}
                        </p>
                      )}
                      {!isMe && employee.mentor_active && (
                        <div className="mt-2">
                          <RequestMentorshipButton mentor={employee} />
                        </div>
                      )}
                    </div>

                    {/* Mentee info */}
                    <div className="p-3 rounded-lg border border-border bg-zinc-50 dark:bg-zinc-800/40">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="text-[11px] uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400">
                          {isMe ? "Vreau să învăț" : `${employee.name.split(" ")[0]} vrea să învețe`}
                        </div>
                        <button
                          data-testid="open-mentee-editor"
                          onClick={() => setEditingMentee(true)}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border border-border hover:bg-white dark:hover:bg-zinc-800"
                        >
                          <BookOpen className="h-3 w-3" /> Editează
                        </button>
                      </div>
                      {(employee.mentee_skills || []).length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {(employee.mentee_skills || []).map((s) => (
                            <span key={s} className="inline-flex items-center px-2 py-0.5 text-[11px] font-medium rounded-md bg-sky-100 dark:bg-sky-900/30 text-sky-800 dark:text-sky-300">
                              {s}
                            </span>
                          ))}
                        </div>
                      ) : (
                        <p className="mt-1.5 text-[11px] text-zinc-500 dark:text-zinc-400 italic">
                          {isMe ? "Adaugă competențele pe care vrei să le înveți." : "Nu a setat interese de învățare."}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </section>
              <section>
                <h4 className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-3">
                  Manager
                </h4>
                {manager ? (
                  <button
                    data-testid="profile-manager-link"
                    onClick={() => onOpenOther(manager.id)}
                    className="w-full flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-zinc-50 dark:hover:bg-zinc-800/60 transition-colors duration-150 text-left"
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold ${deptAvatarClass(manager.department)}`}>
                      {manager.initials}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">{manager.name}</div>
                      <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{manager.role}</div>
                    </div>
                    <ArrowUpRight className="h-4 w-4 text-zinc-400" />
                  </button>
                ) : (
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">Nu raportează către alt angajat.</p>
                )}
              </section>

              {/* Reports */}
              <section>
                <h4 className="text-xs uppercase tracking-[0.2em] font-medium text-zinc-500 dark:text-zinc-400 mb-3">
                  Echipa {reports.length > 0 && <span className="ml-1 text-zinc-400">({reports.length})</span>}
                </h4>
                {reports.length === 0 ? (
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">Nu are echipă.</p>
                ) : (
                  <ul data-testid="profile-reports-list" className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {reports.map((r) => (
                      <li key={r.id}>
                        <button
                          data-testid={`profile-report-${r.id}`}
                          onClick={() => onOpenOther(r.id)}
                          className="w-full flex items-center gap-3 p-2 rounded-lg border border-border hover:bg-zinc-50 dark:hover:bg-zinc-800/60 transition-colors duration-150 text-left"
                        >
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold ${deptAvatarClass(r.department)}`}>
                            {r.initials}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100 truncate">{r.name}</div>
                            <div className="text-xs text-zinc-500 dark:text-zinc-400 truncate">{r.role}</div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-zinc-400 flex-shrink-0" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
